import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { fetchProfileByHandle, fetchProfileById } from '../lib/queries';
import { ProfileStripeLogo } from './Profile';
import { ProfileCard } from '../components/ProfileCard';
import { Button } from '../components/ui';
import type { Profile } from '../lib/types';
import './Profile.css';

/**
 * Public view of another member's profile (/u/:handle). Mirrors the owner's own
 * read view (Profile.tsx, .profile--me) - stripe + identity card with avatar,
 * name, @handle, badges, location, socials, bio - minus everything private or
 * owner-only: no stats, no recent posts, no banner, no edit/billing/sign-out.
 */
export default function PublicProfile() {
  const { handle = '' } = useParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        // Accept a user UUID as a fallback path segment, so admin-links work
        // even before the user sets a handle.
        const looksLikeUuid = /^[0-9a-f-]{8,}$/i.test(handle) && handle.length > 20;
        const p = looksLikeUuid
          ? await fetchProfileById(handle, ctrl.signal)
          : await fetchProfileByHandle(handle, ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (!p) { setNotFound(true); return; }
        setProfile(p);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[public-profile] failed', err);
        setNotFound(true);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [handle]);

  if (loading) {
    return (
      <div className="profile profile--me" aria-busy="true" aria-label="Loading profile">
        <aside className="profile__stripe" aria-hidden="true">
          <ProfileStripeLogo />
        </aside>
        <main className="profile__pane container">
          <div className="profile__pane-inner">
            <div className="profile__identity">
              <Skeleton variant="avatar" width={112} height={112} />
              <div style={{ height: 'var(--space-4)' }} />
              <Skeleton variant="text" width={220} height={28} />
              <div style={{ height: 'var(--space-2)' }} />
              <Skeleton variant="text" width="40%" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="container">
        <EmptyState
          eyebrow="Not found"
          headline="No one here by that handle."
          body="They may not have signed up yet, or the link might be off."
          cta={<Button as={Link} to="/" variant="soft" size="sm">← Back to the feed</Button>}
        />
      </div>
    );
  }

  return (
    <div className="profile profile--me">
      <aside className="profile__stripe" aria-hidden="true">
        <ProfileStripeLogo />
      </aside>

      <main className="profile__pane container">
        <div className="profile__pane-inner">
          <p className="kicker profile__eyebrow">Profile</p>
          <ProfileCard profile={profile} />
        </div>
      </main>
    </div>
  );
}
